import type { ModelDeleteButtonProps } from "./model-delete-button.type";
import type { ButtonProps } from "antd";

import { DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { useAuthorization } from "@/modules/authorization";
import { getModelTranslationKey, type ModelItem } from "@/modules/model";
import { useModelDelete } from "@/modules/model-delete";
import { ButtonWithTooltip } from "@/ui/button-with-tooltip";

const ModelDeleteButton = <Item extends ModelItem>({
  modelKey,
  item,
  translationKey,
  onClick,
  invalidateKeys,
  authorizeRender = "hidden",
  hideDanger,
  hideIcon,
  ...buttonProps
}: ModelDeleteButtonProps<Item>) => {
  const { t } = useTranslation();
  const { openDelete } = useModelDelete();
  const { canDelete } = useAuthorization();

  if (!canDelete(item) && authorizeRender === "hidden") return null;

  const onDeleteClick: ButtonProps["onClick"] = (e) => {
    const translationBase = translationKey ?? getModelTranslationKey(modelKey);
    const title = t(`${translationBase}:delete.label`);

    openDelete({ modelKey, item, title, invalidateKeys });
    onClick?.(e);
  };

  return (
    <ButtonWithTooltip
      danger={!hideDanger}
      icon={!hideIcon ? <DeleteOutlined /> : undefined}
      disabled={!canDelete(item) && authorizeRender === "disabled"}
      onClick={onDeleteClick}
      {...buttonProps}
    >
      {t("global:delete")}
    </ButtonWithTooltip>
  );
};

export default ModelDeleteButton;
